"use client";

import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import { TextD2C } from "../../elements/text-d2c/text";
import { deriveD2CTextColors } from "../../lib/d2c-colors";
import { cn } from "../../lib/utils";
import { Carousel } from "../../patterns/carousel/carousel";
import { ArrangementImage } from "./arrangement-image";
import type { ImageArrangementProps } from "./image-arrangement-types";

interface RowImageArrangementProps
  extends Pick<
    ImageArrangementProps,
    | "background"
    | "fullWidth"
    | "headline"
    | "images"
    | "nextSlideText"
    | "previousSlideText"
    | "readMoreLabel"
    | "readMoreModalTitle"
    | "title"
  > {
  hideCarouselNavigationArrows?: boolean;
  staticLayoutThreshold: number;
  headlineInspectorProps?: Record<string, string>;
}

export function RowImageArrangement(props: Readonly<RowImageArrangementProps>) {
  const isDarkBackground = props.background === "black";
  const isStaticLayout = props.images.length <= props.staticLayoutThreshold;
  const fullWidth = props.fullWidth ?? true;

  const { headlineColor } = deriveD2CTextColors(props.background);

  const headline = props.headlineInspectorProps ? (
    <TextD2C
      color={headlineColor}
      dash={false}
      variant="h4"
      {...props.headlineInspectorProps}
    >
      <RichTextRenderer richText={props.headline?.headline?.json} />
    </TextD2C>
  ) : (
    <TextD2C color={headlineColor} dash={false} variant="h4">
      <RichTextRenderer richText={props.headline?.headline?.json} />
    </TextD2C>
  );

  const carousel = (
    <div data-testid="image-arrangement-row">
      <Carousel
        disableDragWhenNotScrollable
        dragFree
        nextSlideText={props.nextSlideText || "Next image"}
        previousSlideText={props.previousSlideText || "Previous image"}
        progressIndicatorClassName={cn(isDarkBackground && "bg-white")}
        progressTrackClassName={cn(isDarkBackground && "bg-white/30")}
        showNavigationArrows={!props.hideCarouselNavigationArrows}
        showProgressBar
        trackClassName="ml-0"
        wheelGestures
      >
        {props.images.map((image, index) => (
          <div
            className="mx-2 min-w-0 shrink-0 grow-0 basis-[85vw] first:ml-0 last:mr-auto sm:basis-80 md:basis-[22.5rem] lg:basis-[25rem]"
            data-testid="image-arrangement-row-item"
            key={`${image.src}-${index}`}
          >
            <div className="mx-auto w-full max-w-[400px]">
              <ArrangementImage
                background={props.background}
                image={image}
                readMoreLabel={props.readMoreLabel}
                readMoreModalTitle={props.readMoreModalTitle}
                sizes="(min-width: 640px) 400px, 85vw"
              />
            </div>
          </div>
        ))}
      </Carousel>
    </div>
  );

  const title = props.title ? (
    <TextD2C color={headlineColor} tagName="h2" variant="h5">
      {props.title}
    </TextD2C>
  ) : null;

  return (
    <>
      {isStaticLayout ? (
        <>
          <div className={cn("md:hidden")}>
            {title}
            {headline}
            {carousel}
          </div>

          <div className={cn(fullWidth && "mx-auto w-fit", "hidden md:block")}>
            {title}
            {headline}
            <div className="flex gap-10" data-testid="image-arrangement-row">
              {props.images.map((image, index) => (
                <div
                  className="basis-[364px]"
                  data-testid="image-arrangement-row-item"
                  key={`${image.src}-${index}`}
                >
                  <div className="mx-auto w-full max-w-[364px]">
                    <ArrangementImage
                      background={props.background}
                      image={image}
                      readMoreLabel={props.readMoreLabel}
                      readMoreModalTitle={props.readMoreModalTitle}
                      sizes="(min-width: 640px) 364px, 85vw"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {title}
          {headline}
          {carousel}
        </>
      )}
    </>
  );
}
